
import { useState, useRef, useEffect } from 'react';

interface SandboxModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SandboxModal({ isOpen, onClose }: SandboxModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setError('');
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formRef.current) return;

    const formData = new FormData(formRef.current);
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const company = formData.get('company') as string;

    if (!name.trim() || !email.trim() || !company.trim()) {
      setError('All fields are required.');
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.append('name', name.trim());
      body.append('email', email.trim());
      body.append('company', company.trim());

      await fetch('https://readdy.ai/api/form/d69umo5bd17lru29lg2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
    >
      <div className="bg-stone-50 w-full max-w-md p-10 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-stone-500 hover:text-stone-900 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 text-red-700">
              <i className="ri-check-line text-3xl"></i>
            </div>
            <h3 className="text-2xl font-serif text-stone-900 mb-3">You\u2019re in.</h3>
            <p className="text-stone-600 leading-relaxed">
              We\u2019ll reach out within 24 hours<br />to set up your sandbox.
            </p>
            <button
              onClick={onClose}
              className="mt-8 px-8 py-3 bg-stone-900 text-stone-50 text-sm font-medium hover:bg-stone-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-2xl font-serif text-stone-900 mb-2">Enter the Sandbox</h3>
            <p className="text-sm text-stone-600 mb-8">Contained AI. $200/month.</p>

            <form
              ref={formRef}
              id="sandbox-signup-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-500 mb-2">Full Name</label>
                <input
                  type="text"
                  name="name"
                  className="w-full border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-500 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  className="w-full border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-stone-500 mb-2">Company</label>
                <input
                  type="text"
                  name="company"
                  className="w-full border border-stone-300 bg-white px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-900 transition-colors"
                  placeholder="Your organization"
                />
              </div>

              {error && <p className="text-red-700 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-stone-900 text-stone-50 text-sm font-medium hover:bg-stone-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {submitting ? 'Submitting\u2026' : 'Enter the Sandbox'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
